import { Attr, BelongsTo, HasMany, Model } from "spraypaint";

import { type SOCIAL_ENGAGE_POST_STATES } from "shared/constants";
import { ApplicationRecord } from "shared/services/spraypaint";
import type { ValueOf } from "shared/types";

import type ActiveStorageAttachment from "./ActiveStorageAttachment";
import type Organisation from "./Organisation";
import type OrganisationSocialIntegration from "./OrganisationSocialIntegration";
import type OrganisationTeam from "./OrganisationTeam";
import type SocialEngagePostEvent from "./SocialEngagePostEvent";
import type SocialEngagePostsIntegration from "./SocialEngagePostsIntegration";
import type Tag from "./Tag";
import type { ErrorWithResponse } from "./types";
import type User from "./User";

@Model()
class SocialEngagePost extends ApplicationRecord {
  static jsonapiType = "social_engage_posts";

  @HasMany() activeStorageAttachments: ActiveStorageAttachment[];

  @BelongsTo() approver: User;

  @Attr() approverId: number;

  @Attr() archived: boolean;

  @Attr({ persist: false }) archivedAt: string;

  @Attr() content: string;

  @Attr({ persist: false }) createdAt: string;

  @Attr({ persist: false }) createdBySuperUser: boolean;

  @Attr() facebookText: string;

  @Attr() immediate: boolean;

  @Attr() individualPostTimePerAccount: number;

  @Attr() instagramText: string;

  @Attr() integrationPerformanceTotal: number;

  @Attr() linkedinText: string;

  @Attr() mentions: Record<string, any>;

  @Attr() native: boolean;

  @BelongsTo() organisation: Organisation;

  @Attr() organisationId: number;

  @HasMany()
  organisationSocialIntegrations: OrganisationSocialIntegration[];

  @BelongsTo() organisationTeam: OrganisationTeam;

  @Attr() organisationTeamId: number;

  @Attr({ persist: false }) postType: string;

  @Attr() scheduledAt: string;

  @HasMany() socialEngagePostEvents: SocialEngagePostEvent[];

  @HasMany()
  socialEngagePostsIntegrations: SocialEngagePostsIntegration[];

  @Attr() status: ValueOf<typeof SOCIAL_ENGAGE_POST_STATES>;

  @HasMany({ name: "taggable" }) tags: Tag[];

  @Attr() title: string;

  @Attr() twitterText: string;

  @Attr({ persist: false }) updatedAt: string;

  @BelongsTo() user: User;

  @Attr() userId: number;

  static notArchived() {
    return this.where({ archivedAt: { eq: "null" } });
  }
}

SocialEngagePost.afterFetch = (response, json: JSON & { message?: string }) => {
  if (response.status === 401 && json.message === "sudo access required") {
    const error: ErrorWithResponse = new Error("sudo access required");
    error.response = Object.assign(response, { data: json });

    throw error;
  } else {
    ApplicationRecord.middlewareStack.afterFetch(response, json);
  }
};

export default SocialEngagePost;
